import {Component, OnInit} from '@angular/core';
import {OAuthService} from 'angular-oauth2-oidc';
import {Router} from '@angular/router';
import {ActivatedRoute} from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/core/auth/auth.service';
import { ValidateTokenDto } from 'src/app/core/auth/dtos/validate-token.dto';
import { ValidateCodeVerifierDto } from 'src/app/core/auth/dtos/validate-codeverifier.dto';
import { environment as env } from '@env';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {SESSION_STORAGE_KEYS} from '@shared/constants/session-storage';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit {

  isLoading = false;
  error: string;
  message: string;
  validateToken: ValidateTokenDto;
  validateCodeVerifier: ValidateCodeVerifierDto;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private oauthService: OAuthService,
    private authService: AuthService,
    private readonly sessionStorageService: SessionStorageService
  ) {
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const token: string | undefined = params['externalToken'];
      const scope: string | undefined = params['scope'];
      const state: string | undefined = params['state'];
      const redirect: string | undefined = params['redirect'];

      if (token) {
        this.externalToken(token);
        return;
      }

      if (this.hasRejectedAuthBridgeScope(scope)) {
        this.loginBridge(state);
        return;
      }

      if (this.isFirstAccess(redirect)) {
        this.router.navigate(['/primeiro-acesso']);
        return;
      }

      this.login();
    });
  }

  private hasRejectedAuthBridgeScope(scope: string | undefined): boolean {
    return !!scope && scope.split(' ').includes('tm_authbridge_rejected_api');
  }

  private isFirstAccess(redirect: string | undefined): boolean {
    return redirect === '/?action=firstAccess'
  }

  async loginBridge(state: string): Promise<void> {

    this.validateCodeVerifier = await this.authService.validateCodeVerifier(state);

    localStorage.setItem('state', state);
    localStorage.setItem('nonce', state);
    localStorage.setItem('PKCE_verifier', this.validateCodeVerifier.codeVerifier);

    await this.authService.callbackLoginBridge();

    if (this.oauthService.hasValidAccessToken()) {
      this.router.navigate(['/login/auth-bridge-success'], {
        queryParams: {},
        replaceUrl: true
      });
    } else {
      this.redirectToWelcomePage();
    }
  }

  async login(): Promise<void> {
    this.isLoading = true;
    this.message = 'Verificando sessão.';

    try {
      await this.authService.callbackLogin();

      if (this.oauthService?.hasValidAccessToken()) {
        
       const urlRedirect = this.sessionStorageService.get(SESSION_STORAGE_KEYS.edenredOneToPage);
       const productOne = await firstValueFrom(this.authService.getHasProductOne());

       if (this.shouldRedirectToEdenredOne(urlRedirect, productOne?.hasProductOne)) {
          window.location.href = env.urlEdenredOne;
          return;
        }

        this.router.navigate(['/selecionar-perfil'], {
          queryParams: {},
          replaceUrl: true
        });

      } else {
        this.redirectToWelcomePage();
      }
    } catch (error) {
      this.error = 'Não foi possível inicializar sua sessão.';
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  private shouldRedirectToEdenredOne(urlRedirect: string | null, hasProductOne?: boolean): boolean {
    return !!hasProductOne && (!urlRedirect || urlRedirect.length === 0);
  }

  async externalToken(token: string) {
    this.isLoading = true;
    this.message = 'Validando token externo.';

    try {
      this.validateToken = await this.authService.validateExternalToken(token);

      if (!this.validateToken.success) {
        this.message = 'Não foi possível inicializar sua sessão com o token externo.';
        console.error('Token inválido ou expirado.');
        return;
      }

      this.oauthService.setStorage(localStorage);
      localStorage.setItem('access_token', this.validateToken.code);

      await this.oauthService.tryLogin();

      if (this.oauthService.hasValidAccessToken()) {
        this.router.navigate(['/selecionar-perfil'], {
          queryParams: {},
          replaceUrl: true
        });
      } else {
        console.error('Token inválido ou expirado.');
      }

    } catch (error) {
      this.error = 'Não foi possível inicializar sua sessão com o token externo.';
    } finally {
      this.isLoading = false;
    }
  }

  redirectToWelcomePage() {
    this.message = 'Redirecionando para tela de login.';
    this.router.navigate(['/login/bem-vindo']);
  }

  tryAgain() {
    this.oauthService.logOut();
  }

}
